import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BadgeDollarSign, HeartHandshake, Loader2, ShieldCheck, Info, MapPin, Landmark, Building2, Code2, Eye, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { createSupportCheckout, getSupportStats } from "@/lib/api";

const presetTiers = [
  {
    tier: "monthly_5",
    amount: "$5",
    cadence: "/month",
    title: "Keeps the servers running",
    body: "A small monthly push that helps keep Measure X-Ray available without ads.",
    testId: "support-tier-monthly-5",
  },
  {
    tier: "monthly_10",
    amount: "$10",
    cadence: "/month",
    title: "Funds deeper analyses",
    body: "Helps cover the boring-but-real costs behind evidence-first civic tooling.",
    testId: "support-tier-monthly-10",
  },
];

const formatCount = (value) => Number(value || 0).toLocaleString();

export default function SupportPage() {
  const [anonymous, setAnonymous] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [customAmount, setCustomAmount] = useState("15");
  const [customFrequency, setCustomFrequency] = useState("monthly");
  const [loadingTier, setLoadingTier] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    getSupportStats()
      .then((data) => {
        if (alive) setStats(data);
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      alive = false;
    };
  }, []);

  const supporterCount = useMemo(
    () => formatCount(stats?.active_supporter_count || stats?.lifetime_supporter_count || 0),
    [stats]
  );

  const checkout = async (payload, key) => {
    setLoadingTier(key);
    try {
      const data = await createSupportCheckout({
        ...payload,
        anonymous,
        display_name: anonymous ? undefined : displayName.trim() || undefined,
      });
      if (!data?.checkout_url) throw new Error("Missing checkout URL");
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not start Stripe checkout.");
      setLoadingTier(null);
    }
  };

  const submitCustom = async (e) => {
    e.preventDefault();
    const dollars = Number(customAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error("Enter a support amount greater than $0.");
      return;
    }
    const cents = Math.round(dollars * 100);
    await checkout(
      {
        tier: customFrequency === "monthly" ? "custom_monthly" : "one_time",
        amount_cents: cents,
      },
      "custom"
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-16" data-testid="support-page">
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <span className="stamp stamp-explicit">Ad-free</span>
          <span className="stamp stamp-implied">Anonymous by default</span>
        </div>

        <h1 className="hero-title text-4xl sm:text-5xl lg:text-6xl text-[hsl(var(--ink))] mb-5 max-w-4xl">
          This project runs on voter support, not ads or donor data.
        </h1>

        <p className="text-base sm:text-lg text-[hsl(var(--ink-2))] max-w-2xl leading-relaxed">
          Measure X-Ray stays useful by staying boring about money: voluntary support,
          no feature gates, no tracking pixels, no campaign donors buying influence.
        </p>

        <div className="mt-5">
          <FundingExplainerDialog />
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4 mb-6" aria-label="Support tiers">
        {presetTiers.map((item) => (
          <button
            key={item.tier}
            type="button"
            data-testid={item.testId}
            onClick={() => checkout({ tier: item.tier }, item.tier)}
            disabled={!!loadingTier}
            className="paper-card-elevated rounded-sm p-5 text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="section-label mb-1">Monthly support</div>
                <div className="font-serif text-4xl font-semibold tracking-tight">
                  {item.amount}
                  <span className="text-base font-sans font-normal text-[hsl(var(--ink-muted))]">
                    {item.cadence}
                  </span>
                </div>
              </div>
              {loadingTier === item.tier ? (
                <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--accent-blue))]" />
              ) : (
                <HeartHandshake className="w-5 h-5 text-[hsl(var(--ink-muted))]" />
              )}
            </div>
            <div className="font-semibold mb-2">{item.title}</div>
            <p className="text-sm text-[hsl(var(--ink-2))] leading-relaxed mb-4">{item.body}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--accent-blue))]">
              Support at {item.amount} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </button>
        ))}

        <form
          onSubmit={submitCustom}
          data-testid="support-custom-form"
          className="paper-card-elevated rounded-sm p-5"
        >
          <div className="section-label mb-1">Choose your amount</div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl font-serif font-semibold">$</span>
            <input
              type="number"
              min="1"
              step="1"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              data-testid="support-custom-amount-input"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-2xl font-serif focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
              aria-label="Custom support amount"
            />
          </div>

          <div className="flex rounded-full border border-[hsl(var(--rule))] p-1 mb-4" data-testid="support-frequency-toggle">
            <button
              type="button"
              onClick={() => setCustomFrequency("monthly")}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                customFrequency === "monthly"
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))]"
                  : "text-[hsl(var(--ink-muted))]"
              }`}
              data-testid="support-frequency-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setCustomFrequency("one_time")}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                customFrequency === "one_time"
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))]"
                  : "text-[hsl(var(--ink-muted))]"
              }`}
              data-testid="support-frequency-one-time"
            >
              One-time
            </button>
          </div>

          <button
            type="submit"
            disabled={!!loadingTier}
            data-testid="support-submit-button"
            className="pill-btn pill-btn-primary w-full justify-center disabled:opacity-60"
          >
            {loadingTier === "custom" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeDollarSign className="w-4 h-4" />}
            Continue to Stripe
          </button>
        </form>
      </section>

      <section className="paper-card rounded-sm p-5 mb-6" data-testid="support-identity-options">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
            data-testid="support-anonymous-toggle"
            className="mt-1 h-4 w-4 accent-[hsl(var(--ink))]"
          />
          <span>
            <span className="block font-semibold">Anonymous by default</span>
            <span className="block text-sm text-[hsl(var(--ink-muted))] mt-0.5">
              Measure X-Ray does not publish supporter names or donor lists.
            </span>
          </span>
        </label>

        {!anonymous && (
          <div className="mt-4 max-w-md">
            <label className="section-label block mb-2" htmlFor="support-display-name">
              Optional display name
            </label>
            <input
              id="support-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="support-display-name-input"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
              placeholder="Name or initials"
            />
          </div>
        )}
      </section>

      <section className="paper-card-elevated rounded-sm p-5 sm:p-6" data-testid="support-transparency-pledge">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[hsl(var(--accent-green))] shrink-0 mt-0.5" />
          <div>
            <div className="section-label mb-2">Transparency pledge</div>
            <p className="text-sm sm:text-base text-[hsl(var(--ink-2))] leading-relaxed">
              Currently funded by <strong>{supporterCount} voters</strong>. No campaign, candidate,
              or corporate donor has ever paid for content on Measure X-Ray.
            </p>
            <p className="text-xs text-[hsl(var(--ink-muted))] mt-3">
              Contributions support the tool. They do not buy coverage, ranking, language, access, or conclusions.
            </p>
          </div>
        </div>
      </section>

      <div className="mt-8 text-sm text-[hsl(var(--ink-muted))]">
        Need a deeper custom report? <Link to="/dossier" className="ink-underline text-[hsl(var(--ink))]">Request a dossier</Link>.
      </div>
    </div>
  );
}


function FundingExplainerDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="support-funding-explainer-trigger"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-[hsl(var(--ink))] text-sm font-medium hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--paper))] transition-colors"
        >
          <Info className="w-4 h-4" />
          What does my support fund?
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="support-funding-explainer-dialog"
        className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[hsl(var(--paper))]"
      >
        <DialogHeader>
          <div className="section-label mb-2" style={{ color: "hsl(var(--accent-blue))" }}>
            Where the money goes
          </div>
          <DialogTitle className="font-serif text-2xl sm:text-3xl leading-tight">
            Voter support is what lets this scale.
          </DialogTitle>
          <DialogDescription className="text-sm text-[hsl(var(--ink-2))] leading-relaxed pt-2">
            Measure X-Ray is built and run by one person. Every dollar of support
            goes directly into making the tool more useful to voters &mdash; never to
            ads, never to investors, never to campaigns. Here&rsquo;s the actual
            roadmap your support unlocks.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-5 text-sm text-[hsl(var(--ink-2))]">
          <FundingItem
            icon={<Eye className="w-4 h-4" />}
            tier="Right now"
            title="Keeping the lights on"
            body="Server costs, AI analysis costs (each bill scan costs real cents in API spend), and the time to keep the California pipeline updated as bills move and laws change."
          />
          <FundingItem
            icon={<MapPin className="w-4 h-4" />}
            tier="Next milestone"
            title="California county &amp; city ballot measures"
            body="Right now Measure X-Ray covers statewide CA propositions and Assembly/Senate bills. Local measures (city council, county supervisors, school boards, water districts) are where most policy actually hits voters &mdash; and where almost nobody publishes good analysis."
          />
          <FundingItem
            icon={<Landmark className="w-4 h-4" />}
            tier="With sustained support"
            title="Federal bills (congress.gov)"
            body="Add a congress.gov adapter to do the same hostile-contract-reviewer treatment on House and Senate bills. Plain-English summary, side-by-side current-law comparison, who-pays/who-gains, conflict detection across companion bills."
          />
          <FundingItem
            icon={<Building2 className="w-4 h-4" />}
            tier="Stretch goal"
            title="All 50 states"
            body="Each state legislature has its own bill text format and quirks. Adding states one at a time, prioritized by the ones with the most ballot-measure activity (Oregon, Florida, Arizona, Colorado, Washington, Texas)."
          />
          <FundingItem
            icon={<Users className="w-4 h-4" />}
            tier="Long term"
            title="Make it survive me"
            body="If this becomes meaningful, transition it to a small nonprofit with a tiny editorial team so it doesn&rsquo;t live or die based on one person&rsquo;s availability. Open-source the analysis pipeline so other civic tools can build on it."
          />
          <FundingItem
            icon={<Code2 className="w-4 h-4" />}
            tier="Always"
            title="Quality &amp; honesty"
            body="More accurate quote extraction, fewer LLM hallucinations, better source coverage, and a published methodology so journalists and researchers can trust the output. No upselling, no premium tier hiding the good analysis behind a paywall."
          />
        </div>

        <div
          className="mt-6 pt-4 border-t border-dashed border-[hsl(var(--rule))]"
          data-testid="support-funding-promise"
        >
          <div className="section-label mb-2">The promise</div>
          <ul className="text-xs text-[hsl(var(--ink-2))] space-y-1.5 list-disc pl-5">
            <li>The free analysis stays 100% free for voters. Forever.</li>
            <li>No ads, ever.</li>
            <li>No tracking pixels or analytics that follow users around.</li>
            <li>No campaign or corporate donor money. Voter support only.</li>
            <li>No donor lists. No leaderboards. Anonymous by default.</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FundingItem({ icon, tier, title, body }) {
  return (
    <div className="flex items-start gap-3" data-testid={`support-funding-item-${tier.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mt-0.5 w-7 h-7 rounded-sm bg-[hsl(var(--paper-2))] border border-[hsl(var(--rule))] flex items-center justify-center text-[hsl(var(--ink))] shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[hsl(var(--ink-muted))] mb-0.5">
          {tier}
        </div>
        <div className="font-semibold text-[hsl(var(--ink))] mb-1 leading-snug">{title}</div>
        <p className="text-sm text-[hsl(var(--ink-2))] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
